import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Grid,
  Header,
  Link,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import { LoadingBar } from '@amzn/awsui-chat-components';
import moment from 'moment';

import styles from './NewAiEvent.module.scss';
import waveStyles from '@/src/styles/wave-loading.module.scss';
import { useChallenges } from '@/src/store/challenge.context';
import { ChallengeDescriptor, ChallengeListItem } from '@/src/types/Challenge';
import { EditEventActions, useEditEvent } from '@/src/store/edit-event.context';
import { EventAudienceType } from '@/src/constants/shared';
import { EventCreationType } from '@/src/constants/shared/event-type';
import { FeatureName, useFeatureUsageTracker } from '@/src/utils/engagement/engagement-tracker';
import { useApi } from '@/src/store/api.context';
import { ChallengesAPI } from '@/src/api/ChallengesAPI';
import { useHistory, useLocation } from 'react-router-dom';
import { RoutePath } from '@/src/RoutePath';
import { getTimezonesWithUTCOffsetAsOptionList } from '@/src/utils/event-time.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { useTranslation } from 'react-i18next';
import { MeridiemOptions } from '@/src/constants/DateTimeConstants';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useEventCreateStore } from '@/src/components/events/useEventCreateStore';
import { AIEventCreateForm } from '@/src/components/events/AIEventCreateForm';
import { useUser } from '@/src/store/user.context';
import {
  buildAudienceSelectOptionsForUser,
  getAvailablePrivacyTypesForUser,
  getDefaultAudienceForGroup,
} from '@/src/components/events/eventsCommon/EventChannelTypes';
import { EventPrivacyType } from '@/src/types/EventPrivacyType';
import useDebounceCallback from '@/src/hooks/useDebounceCallback';
import { useUserWebsocket } from '@/src/hooks/useUserWebsocket';
import { config } from '@/src/config/app-config';
import { splitTextToSpans } from '@/src/utils/dom.utils';

const difficultyToString = (difficulty: number) => {
  return difficulty === 0 ? 'Fundamental' : difficulty === 1 ? 'Intermediate' : 'Advanced';
};

interface DragItem {
  challengeId: string | null;
  index: number;
}

const TIMEZONES = getTimezonesWithUTCOffsetAsOptionList();

const EMPTY_OPTION = { value: '' };

interface AsyncRecommendationsResult {
  submitAsync: (prompt: string) => void;
}

const useAsyncChallengeRecommendations = ({
  challengesApi,
  onSuccess,
  onError,
  onThrottled,
  addErrorFlashbar,
  t,
}: {
  challengesApi: ChallengesAPI;
  onSuccess: (ids: string[]) => void;
  onError: () => void;
  onThrottled: () => void;
  addErrorFlashbar: (msg: string) => void;
  t: (key: string) => string;
}): AsyncRecommendationsResult => {
  const { onAsyncResult, offAsyncResult, fetchAsyncResultFromS3 } = useUserWebsocket();
  const pendingJobIdRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const POLL_INTERVAL_MS = 5000;
  const POLL_DELAY_MS = 30000;
  const POLL_TIMEOUT_MS = 120000;

  const stopPolling = () => {
    if (pollingDelayRef.current) {
      clearTimeout(pollingDelayRef.current);
      pollingDelayRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  const startPolling = (s3Url: string, onResult: (data: { recommendedChallengeIds: string[] }) => void) => {
    stopPolling();
    pollingDelayRef.current = setTimeout(() => {
      pollingIntervalRef.current = setInterval(() => {
        fetchAsyncResultFromS3(s3Url)
          .then((data) => {
            onResult(data);
          })
          .catch(() => {
            // S3 object not ready yet — keep polling
          });
      }, POLL_INTERVAL_MS);

      pollingTimeoutRef.current = setTimeout(() => {
        stopPolling();
        if (pendingJobIdRef.current) {
          offAsyncResult(pendingJobIdRef.current);
          pendingJobIdRef.current = null;
          addErrorFlashbar(`${t(i18nKeys.apiResponse.error)}`);
          onError();
        }
      }, POLL_TIMEOUT_MS - POLL_DELAY_MS);
    }, POLL_DELAY_MS);
  };

  const submitAsync = (prompt: string) => {
    challengesApi
      .getChallengeRecommendationsFreeText(prompt, true)
      .then((response) => {
        const { jobId, s3Url } = response;
        pendingJobIdRef.current = jobId;

        const handleResult = (data: { recommendedChallengeIds: string[] } | null, error?: Error) => {
          if (!pendingJobIdRef.current) return;
          stopPolling();
          pendingJobIdRef.current = null;
          offAsyncResult(jobId);
          if (error || !data) {
            addErrorFlashbar(`${t(i18nKeys.apiResponse.error)}`);
            onError();
            return;
          }
          if (data.recommendedChallengeIds.length > 0) {
            onSuccess(data.recommendedChallengeIds);
          } else {
            addErrorFlashbar(`${t(i18nKeys.apiResponse.error)}`);
            onError();
          }
        };

        onAsyncResult(jobId, (data, error) => handleResult(data, error));
        startPolling(s3Url, (data) => handleResult(data));
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e?.message);
        if (e.status === 429) {
          onThrottled();
        } else {
          onError();
          addErrorFlashbar(`${t(i18nKeys.apiResponse.error)}`);
        }
      });
  };

  const cleanup = () => {
    stopPolling();
    if (pendingJobIdRef.current) {
      offAsyncResult(pendingJobIdRef.current);
    }
  };

  useEffect(() => {
    return () => cleanup();
  }, [offAsyncResult]);

  return { submitAsync };
};

const NewAiEvent: React.FC<{ onChangeUpdateDelayMS: number }> = ({ onChangeUpdateDelayMS }) => {
  const { t } = useTranslation();
  const { challengesApi } = useApi();
  const { user } = useUser();
  const trackFeatureUsage = useFeatureUsageTracker();
  const { getChallengesByIds, loadingChallenges, selectedChallengesById } = useChallenges();
  const { bulkHandleUpdateEditEvent, handleUpdateEditEvent, newEditedEvent, newEventMode, toggleNewEvent } =
    useEditEvent();
  const history = useHistory();
  const location = useLocation();
  const { addErrorFlashbar } = useFlashbars();
  const { enableAsyncChallengeRecommendations } = config.ff;

  const DURATION_RADIO_GROUP_OPTIONS = ['4', '6', '8'].map((hr) => ({
    label: t(i18nKeys.time.formatted.hours, { amount: hr }),
    value: hr,
  }));

  const dataStore = useEventCreateStore();
  const queryStringUpdater = useDebounceCallback((state) => {
    const params = new URLSearchParams(location.search);
    if (state.eventTimezone?.value) {
      params.set('timezone', state.eventTimezone?.value || '');
    } else {
      params.delete('timezone');
    }
    const dateToPrint = moment(
      state.eventDate + ' ' + state.eventTime + state.selectedMeridiem.value,
      'YYYY-MM-DD hh:mm A'
    )
      .tz(state.eventTimezone?.value || moment.tz.guess())
      .unix();
    params.set('eventDate', dateToPrint.toString());
    if (state.numParticipants) {
      params.set('numParticipants', state.numParticipants);
    } else {
      params.delete('numParticipants');
    }
    if (state.selectedDuration) {
      params.set('duration', state.selectedDuration || '');
    } else {
      params.delete('duration');
    }
    if (state.prompt.length) {
      params.set('prompt', state.prompt);
    } else {
      params.delete('prompt');
    }
    if (state.audienceType?.value) {
      params.set('audience', state.audienceType.value || '');
    } else {
      params.delete('audience');
    }
    if (state.privacyType?.value) {
      params.set('privacy_type', state.privacyType.value || '');
    } else {
      params.delete('privacy_type');
    }
    history.replace({ search: params.toString() });
  }, onChangeUpdateDelayMS);
  useEventCreateStore.subscribe(queryStringUpdater);
  const numParticipants = useEventCreateStore((state) => state.numParticipants);
  const setNumParticipants = useEventCreateStore((state) => state.setNumParticipants);
  const selectedDuration = useEventCreateStore((state) => state.selectedDuration);
  const setSelectedDuration = useEventCreateStore((state) => state.setSelectedDuration);
  const eventDate = useEventCreateStore((state) => state.eventDate);
  const setEventDate = useEventCreateStore((state) => state.setEventDate);
  const eventTime = useEventCreateStore((state) => state.eventTime);
  const setEventTime = useEventCreateStore((state) => state.setEventTime);
  const selectedMeridiem = useEventCreateStore((state) => state.selectedMeridiem);
  const setSelectedMeridiem = useEventCreateStore((state) => state.setSelectedMeridiem);
  const prompt = useEventCreateStore((state) => state.prompt);
  const setPrompt = useEventCreateStore((state) => state.setPrompt);
  const eventTimezone = useEventCreateStore((state) => state.eventTimezone);
  const setEventTimezone = useEventCreateStore((state) => state.setEventTimezone);
  const [recommendedChallenges, setRecommendedChallenges] = useState<string[]>([]);
  const [workflowState, setWorkflowState] = useState<'FORM' | 'LOADING' | 'RESULTS' | 'THROTTLED'>('FORM');

  const { submitAsync } = useAsyncChallengeRecommendations({
    challengesApi,
    onSuccess: (ids) => {
      setRecommendedChallenges(ids);
      setWorkflowState('RESULTS');
    },
    onError: () => setWorkflowState('FORM'),
    onThrottled: () => setWorkflowState('THROTTLED'),
    addErrorFlashbar,
    t,
  });

  const [challengeListItems, setChallengeListItems] = useState<ChallengeListItem[]>([]);
  const [backupChallengeListItems, setBackupChallengeListItems] = useState<ChallengeListItem[]>([]);
  const [excessChallengeListItems, setExcessChallengeListItems] = useState<ChallengeListItem[]>([]);
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    if (!draggedItem) return;

    const newChallengeListItems = [...challengeListItems];
    const draggedChallenge = newChallengeListItems[draggedItem.index];

    // Remove the dragged item from its original position
    newChallengeListItems.splice(draggedItem.index, 1);
    // Insert it at the new position
    newChallengeListItems.splice(dropIndex, 0, draggedChallenge);

    setChallengeListItems(newChallengeListItems);

    // Update the challenge descriptors to match the new order
    const newChallengeDescriptors = newChallengeListItems.map((wrapper) => {
      const cd = new ChallengeDescriptor();
      cd.challengeId = wrapper.challengeId;
      cd.version = ChallengeDescriptor.LATEST_APPROVED_VERSION;
      cd.defaultLabProvider = wrapper.props.defaultLabProvider;
      return cd;
    });

    handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
  };

  const handlePromoteBackupToMain = (challenge: ChallengeListItem, backupIndex: number) => {
    // Remove from backup challenges
    const newBackupWrappers = [...backupChallengeListItems];
    const newBackupDescriptors = [...(newEditedEvent?.backupChallengeConfig?.generalBackups || [])];

    newBackupWrappers.splice(backupIndex, 1);
    newBackupDescriptors.splice(backupIndex, 1);

    // Add to main challenges
    const newChallengeListItems = [...challengeListItems, challenge];

    const cd = new ChallengeDescriptor();
    cd.challengeId = challenge.challengeId;
    cd.version = ChallengeDescriptor.LATEST_APPROVED_VERSION;
    cd.defaultLabProvider = challenge.props.defaultLabProvider;

    const newChallengeDescriptors = [...(newEditedEvent?.challengeDescriptors || []), cd];

    // Update state and context
    setChallengeListItems(newChallengeListItems);
    setBackupChallengeListItems(newBackupWrappers);
    handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
    handleUpdateEditEvent(EditEventActions.GENERAL_BACKUP_CHALLENGES, newBackupDescriptors);
  };

  const handleDemoteMainToBackup = (challenge: ChallengeListItem, mainIndex: number) => {
    // Remove from main challenges
    const newChallengeListItems = [...challengeListItems];
    const newChallengeDescriptors = [...(newEditedEvent?.challengeDescriptors || [])];

    newChallengeListItems.splice(mainIndex, 1);
    newChallengeDescriptors.splice(mainIndex, 1);

    // Add to backup challenges
    const newBackupChallengeListItems = [...backupChallengeListItems, challenge];

    const cd = new ChallengeDescriptor();
    cd.challengeId = challenge.challengeId;
    cd.version = ChallengeDescriptor.LATEST_APPROVED_VERSION;
    cd.defaultLabProvider = challenge.props.defaultLabProvider;

    const newBackupDescriptors = [...(newEditedEvent?.backupChallengeConfig?.generalBackups || []), cd];

    // Update state and context
    setChallengeListItems(newChallengeListItems);
    setBackupChallengeListItems(newBackupChallengeListItems);
    handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
    handleUpdateEditEvent(EditEventActions.GENERAL_BACKUP_CHALLENGES, newBackupDescriptors);
  };

  const handleAddExcessToMain = (challenge: ChallengeListItem, excessIndex: number) => {
    // Remove from excess challenges
    const newExcessChallengeListItems = [...excessChallengeListItems];
    newExcessChallengeListItems.splice(excessIndex, 1);

    // Add to main challenges
    const newChallengeListItems = [...challengeListItems, challenge];

    const cd = new ChallengeDescriptor();
    cd.challengeId = challenge.challengeId;
    cd.version = ChallengeDescriptor.LATEST_APPROVED_VERSION;
    cd.defaultLabProvider = challenge.props.defaultLabProvider;

    const newChallengeDescriptors = [...(newEditedEvent?.challengeDescriptors || []), cd];

    // Update state and context
    setChallengeListItems(newChallengeListItems);
    setExcessChallengeListItems(newExcessChallengeListItems);
    handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
  };

  const handleRemoveChallenge = (idx: number, isBackup = false, isExcess = false) => {
    if (isExcess) {
      // Remove from excess challenges
      const newExcessChallengeListItems = [...excessChallengeListItems];
      newExcessChallengeListItems.splice(idx, 1);
      setExcessChallengeListItems(newExcessChallengeListItems);
    } else if (isBackup) {
      // Remove from backup challenges
      const newBackupChallengeListItems = [...backupChallengeListItems];
      const newBackupDescriptors = [...(newEditedEvent?.backupChallengeConfig?.generalBackups || [])];

      // Get the challenge before removing it
      const challengeToMove = newBackupChallengeListItems[idx];

      // Remove from backup challenges
      newBackupChallengeListItems.splice(idx, 1);
      newBackupDescriptors.splice(idx, 1);

      // Add to excess challenges
      const newExcessChallengeListItems = [...excessChallengeListItems, challengeToMove];

      // Update state
      setBackupChallengeListItems(newBackupChallengeListItems);
      setExcessChallengeListItems(newExcessChallengeListItems);
      handleUpdateEditEvent(EditEventActions.GENERAL_BACKUP_CHALLENGES, newBackupDescriptors);
    } else {
      // Remove from main challenges
      const newChallengeListItems = [...challengeListItems];
      const newChallengeDescriptors = [...(newEditedEvent?.challengeDescriptors || [])];

      // Get the challenge before removing it
      const challengeToMove = newChallengeListItems[idx];

      // Remove from main challenges
      newChallengeListItems.splice(idx, 1);
      newChallengeDescriptors.splice(idx, 1);

      // Add to excess challenges
      const newExcessChallengeListItems = [...excessChallengeListItems, challengeToMove];

      // Update state
      setChallengeListItems(newChallengeListItems);
      setExcessChallengeListItems(newExcessChallengeListItems);
      handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
    }
  };

  const getChallengeListItem = (c: ChallengeListItem, idx: number, isBackup = false, isExcess = false) => {
    const testIdSuffix = isBackup ? 'backup' : isExcess ? 'excess' : 'main';
    return (
      <div
        key={`challengeListItem${c.challengeId}${idx}`}
        className={styles['challenge-item']}
        data-testid={`challenge-recommendation__item-container-${testIdSuffix}-${idx}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
          }
        }}
        onDrop={(e) => {
          if (!isBackup && !isExcess) {
            handleDrop(e, idx);
          }
        }}
      >
        <div className={styles['challenge-card-wrapper']}>
          <Container>
            <Grid gridDefinition={[{ colspan: 2 }, { colspan: 8 }, { colspan: 2 }]}>
              <ColumnLayout columns={2} borders="vertical">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {!isBackup && !isExcess && (
                    <div
                      draggable
                      onDragStart={(e) => {
                        setDraggedItem({ challengeId: c.challengeId, index: idx });
                        // Add dragging class to the parent container for visual feedback
                        const parentElement = e.currentTarget.closest('.challenge-item');
                        if (parentElement) {
                          parentElement.classList.add('dragging');

                          // Calculate the position of the drag handle within the parent element
                          const handleRect = e.currentTarget.getBoundingClientRect();
                          const parentRect = parentElement.getBoundingClientRect();

                          // Calculate offsets to keep the cursor over the drag handle
                          const xOffset = handleRect.left - parentRect.left + handleRect.width / 2;
                          const yOffset = handleRect.top - parentRect.top + handleRect.height / 2;

                          // Set the drag image to be the entire card container with calculated offsets
                          if (e.dataTransfer && typeof e.dataTransfer.setDragImage === 'function') {
                            e.dataTransfer.setDragImage(parentElement, xOffset, yOffset);
                          }
                        }
                        if (e.dataTransfer) {
                          e.dataTransfer.effectAllowed = 'move';
                        }
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.closest('.challenge-item')?.classList.remove('dragging');
                        setDraggedItem(null);
                      }}
                      className={styles['drag-handle']}
                      style={{ marginRight: '8px', cursor: 'grab' }}
                      data-testid={`challenge-recommendation__drag-handle-${testIdSuffix}-${idx}`}
                    >
                      <div className={styles['drag-handle-line']} />
                      <div className={styles['drag-handle-line']} />
                      <div className={styles['drag-handle-line']} />
                    </div>
                  )}
                  <Box variant="h3" textAlign="center">
                    {idx + 1}
                  </Box>
                </div>
              </ColumnLayout>
              <SpaceBetween direction="vertical" size="m">
                <Link href={`/challenges/${c.challengeId}`} external fontSize="heading-m">
                  {c.props.title}
                </Link>
                <ColumnLayout columns={2}>
                  <Box variant="div">
                    <div>
                      <b>{t(i18nKeys.challenges.browseChallenges.headers.difficulty)}:</b>
                    </div>
                    <div>{difficultyToString(c.props.difficulty)}</div>
                  </Box>
                  <Box variant="div">
                    <div>
                      <b>{t(i18nKeys.eventTemplates.eventTemplateDetails.topic)}:</b>
                    </div>
                    <div>{c.props.jamType}</div>
                  </Box>
                </ColumnLayout>
                <div>
                  {isExcess ? (
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddExcessToMain(c, idx);
                      }}
                    >
                      {t(i18nKeys.challenges.gameBoard.buttons.addToEvent)}
                    </Button>
                  ) : isBackup ? (
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePromoteBackupToMain(c, idx);
                      }}
                      data-testid={`challenge-recommendation__promote-btn-${testIdSuffix}-${idx}`}
                    >
                      {t(i18nKeys.aiEventCreator.promoteToMainLabel)}
                    </Button>
                  ) : (
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDemoteMainToBackup(c, idx);
                      }}
                    >
                      {t(i18nKeys.aiEventCreator.backToBackupLabel)}
                    </Button>
                  )}
                </div>
              </SpaceBetween>
              {!isExcess && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', padding: '10px' }}>
                  <Button
                    variant="icon"
                    iconName="close"
                    onClick={() => handleRemoveChallenge(idx, isBackup, isExcess)}
                    ariaLabel={`${t(i18nKeys.general.remove)} ${c.props.title}`}
                  />
                </div>
              )}
            </Grid>
          </Container>
        </div>
      </div>
    );
  };

  const resetOptions = () => {
    setSelectedDuration('4');
    setPrompt('');
  };

  const audienceTypeOptions = useMemo(() => buildAudienceSelectOptionsForUser(user, t), [user, t]);
  const privacyTypeOptions = useMemo(
    () => getAvailablePrivacyTypesForUser(user, dataStore?.audienceType?.value || '', t),
    [user, t, dataStore.audienceType]
  );

  const firstLoad = useRef(true);

  useEffect(() => {
    dataStore.reset();

    const params = new URLSearchParams(location.search);
    if (!newEventMode) {
      toggleNewEvent();
    }

    const initialTimezone = params.get('timezone') || moment.tz.guess();
    const found = TIMEZONES.find((tmz) => tmz.value === initialTimezone) || null;
    setEventTimezone(found || EMPTY_OPTION);

    const dateFromParams = moment.unix(parseInt(params.get('eventDate') || '', 10));
    const date = dateFromParams.isValid() ? dateFromParams : moment();
    setEventDate(date.format('YYYY-MM-DD'));
    if (!dateFromParams.isValid()) {
      const timeRoundedUp =
        date.minute() || date.second() || date.millisecond()
          ? date.add(2, 'hour').startOf('hour')
          : date.startOf('hour');
      setEventTime(timeRoundedUp.format('hh:mm'));
      setSelectedMeridiem(
        timeRoundedUp.format('A') === MeridiemOptions.AM.value ? MeridiemOptions.AM : MeridiemOptions.PM
      );
    } else {
      setEventTime(dateFromParams.format('hh:mm'));
      setSelectedMeridiem(
        dateFromParams.format('A') === MeridiemOptions.AM.value ? MeridiemOptions.AM : MeridiemOptions.PM
      );
    }

    // loading initial values from query params

    if (parseInt(params.get('numParticipants') || '', 10) > 0) {
      setNumParticipants(params.get('numParticipants') || '');
    } else {
      setNumParticipants('2');
    }

    const durationFromQueryParam = DURATION_RADIO_GROUP_OPTIONS.find((d) => d.value === params.get('duration'));
    if (durationFromQueryParam?.value) {
      setSelectedDuration(durationFromQueryParam?.value);
    } else {
      setSelectedDuration('4');
    }

    setPrompt(params.get('prompt') || '');

    const initialChannel = getDefaultAudienceForGroup(user);
    const channelFromQueryParams = params.get('audience');
    if (channelFromQueryParams || initialChannel) {
      const opt = audienceTypeOptions.find(({ value }) => value === (channelFromQueryParams || initialChannel));
      if (opt) {
        dataStore.setAudienceType(opt);
      }
    }

    const privacyFromQueryParams = params.get('privacy_type');
    dataStore.setPrivacyType(
      privacyTypeOptions.find((priv) => priv.value === privacyFromQueryParams) ||
        privacyTypeOptions.find((priv) => priv.value === EventPrivacyType.PRIVATE_INVITE) || { value: '' }
    );

    const challengesFromQueryParam = (params.get('challenges') || '')?.split(',')?.filter((c) => c && c.trim());
    if (challengesFromQueryParam?.length) {
      setRecommendedChallenges(challengesFromQueryParam);
      setWorkflowState('RESULTS');
    }

    firstLoad.current = false;
  }, []);

  useEffect(() => {
    if (firstLoad.current) return;
    const params = new URLSearchParams(location.search);
    if (recommendedChallenges?.length) {
      params.set('challenges', recommendedChallenges.join(','));
      try {
        void getChallengesByIds(false, false, recommendedChallenges, true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to fetch challenge data', error);
      }
    } else {
      params.delete('challenges');
    }
    history.replace({ search: params.toString() });
  }, [recommendedChallenges]);

  useEffect(() => {
    const newChallengeDescriptors: ChallengeDescriptor[] = [];
    const newChallengeListItems: ChallengeListItem[] = [];

    selectedChallengesById?.forEach((selectedChallenge) => {
      const cd = new ChallengeDescriptor();
      cd.challengeId = selectedChallenge.challengeId;
      cd.version = ChallengeDescriptor.LATEST_APPROVED_VERSION;
      cd.defaultLabProvider = selectedChallenge.props.defaultLabProvider;
      newChallengeDescriptors.push(cd);

      newChallengeListItems.push(selectedChallenge);
    });

    // Take the top recommendations based on the duration of the event
    // General rule is 1 challenge per hour
    const topN = parseInt(selectedDuration, 10);
    if (topN <= newChallengeListItems.length) {
      const remainingListItems = newChallengeListItems.splice(topN);
      const remainingDescriptors = newChallengeDescriptors.splice(topN);

      // Use up to 2 as backup challenges
      const backupListItems = remainingListItems.slice(0, 2);
      const backupDescriptors = remainingDescriptors.slice(0, 2);

      // Use the rest as excess challenges
      const excessListItems = remainingListItems.slice(2);

      handleUpdateEditEvent(EditEventActions.GENERAL_BACKUP_CHALLENGES, backupDescriptors);
      setBackupChallengeListItems(backupListItems);
      setExcessChallengeListItems(excessListItems);
    }

    setChallengeListItems(newChallengeListItems);
    handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
  }, [selectedChallengesById]);

  const onClick = () => {
    setWorkflowState('LOADING');
    setRecommendedChallenges([]);

    const startMoment = moment(`${eventDate} ${eventTime} ${selectedMeridiem.value}`);
    const startDateTime = startMoment.format();
    const endDateTime = startMoment.add(parseInt(selectedDuration, 10), 'hours').format();
    // Handle default values
    const actions = [
      {
        action: EditEventActions.START_AND_END_DATETIMES,
        payload: {
          startDateTime,
          endDateTime,
        },
      },
      {
        action: EditEventActions.TIMEZONE,
        payload: eventTimezone.value,
      },
      {
        action: EditEventActions.TITLE,
        payload: `Gen-AI Created Event ${startMoment.format('YYYY-MM-DD')}`,
      },
      {
        action: EditEventActions.AUDIENCE,
        payload: dataStore.audienceType.value || EventAudienceType.SKILL_BUILDER_EVENT,
      },
      {
        action: EditEventActions.EVENT_PRIVACY_TYPE,
        payload: dataStore.privacyType.value || EventPrivacyType.PRIVATE_INVITE,
      },
      {
        action: EditEventActions.MAX_EXPECTED_PARTICIPANTS,
        payload: numParticipants,
      },
      {
        action: EditEventActions.MIN_EXPECTED_PARTICIPANTS,
        payload: numParticipants,
      },
      {
        action: EditEventActions.EVENT_CREATION_TYPE,
        payload: EventCreationType.AI_POWERED_EVENT,
      },
    ];
    bulkHandleUpdateEditEvent(actions);

    if (enableAsyncChallengeRecommendations) {
      submitAsync(prompt);
    } else {
      // Sync flow (existing behavior)
      challengesApi
        .getChallengeRecommendationsFreeText(prompt)
        .then((recs) => {
          const { recommendedChallengeIds } = recs;
          setRecommendedChallenges(recommendedChallengeIds);
          setWorkflowState('RESULTS');
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e?.message);
          if (e.status === 429) {
            setWorkflowState('THROTTLED');
          } else {
            setWorkflowState('FORM');
            addErrorFlashbar(`${t(i18nKeys.apiResponse.error)}`);
          }
        });
    }
  };

  const reset = () => {
    setRecommendedChallenges([]);
    resetOptions();
    setWorkflowState('FORM');
  };

  const handleCreateEvent = () => {
    if (newEditedEvent) {
      trackFeatureUsage(FeatureName.AI_EVENT_CREATION, 'submit');
      const queryParams = new URLSearchParams();
      queryParams.set('aigenerated', 'true');
      history.push({ pathname: RoutePath.NEW_EVENT, search: queryParams.toString() });
    }
  };

  if (workflowState === 'FORM') {
    return (
      <AIEventCreateForm
        onClick={onClick}
        audienceTypeOptions={audienceTypeOptions}
        privacyTypeOptions={privacyTypeOptions}
      />
    );
  } else if (workflowState === 'LOADING') {
    return (
      <Grid
        gridDefinition={[
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
          { colspan: 10, offset: { xxs: 1 } },
        ]}
      >
        <br />
        <div className={waveStyles['header-image']} />
        <br />
        <div>
          <LoadingBar variant="gen-ai" />
        </div>
        <br />
        <div className={waveStyles['wave-text']}>{t(i18nKeys.aiEventCreator.loadingStateTitle)}</div>
        <div className={waveStyles['wave-text']}>
          🧑‍🍳 {t(i18nKeys.aiEventCreator.loadingStateContent)}
          {splitTextToSpans('...')}
        </div>
      </Grid>
    );
  } else if (workflowState === 'THROTTLED') {
    return (
      <SpaceBetween direction="vertical" alignItems="center" size="m">
        <Box variant="div" padding={{ top: 'l' }}>
          <div className="header-image" />
        </Box>
        <Box variant="h3">🛑 {t(i18nKeys.aiEventCreator.throttleContent)}</Box>
        <SpaceBetween direction="horizontal" size="l">
          <Button onClick={reset} variant="link">
            {t(i18nKeys.aiEventCreator.startOver)}
          </Button>
          <Button onClick={onClick}>
            {t(i18nKeys.codeEditor.errorStateRecovery)}
            <b>
              <i>?</i>
            </b>
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    );
  } else {
    return (
      <Container
        header={
          <Box>
            <Box float="left">
              <SpaceBetween direction="horizontal" size="l">
                <Box variant="h2" padding={{ bottom: 'l', top: 'l' }}>
                  {t(i18nKeys.aiEventCreator.challengeSuggestions)}
                </Box>
                <Box variant="div" padding={{ bottom: 'l', top: 'l' }}>
                  <Button disabled={loadingChallenges} onClick={onClick}>
                    {t(i18nKeys.aiEventCreator.drawNewSuggestions)}
                  </Button>
                </Box>
              </SpaceBetween>
            </Box>
            <Box float="right" padding={'m'}>
              <SpaceBetween direction="horizontal" size="l">
                <Button onClick={reset}>{t(i18nKeys.general.cancel)}</Button>
                <Button variant="primary" onClick={handleCreateEvent}>
                  {t(i18nKeys.eventTemplates.catalogDetails.submitButton.confirm)}
                </Button>
              </SpaceBetween>
            </Box>
          </Box>
        }
      >
        {loadingChallenges ? (
          <Box variant="p">
            <Spinner /> {t(i18nKeys.aiEventCreator.spinner)}
          </Box>
        ) : (
          <Grid gridDefinition={[{ colspan: 7 }, { colspan: 4, offset: { xxs: 1 } }]}>
            {/* Main column - Event challenges and backup challenges */}
            <SpaceBetween direction="vertical" size="l">
              <Box variant="h3">{t(i18nKeys.aiEventCreator.eventChallenges)}</Box>
              {challengeListItems.map((c, idx) => getChallengeListItem(c, idx, false))}

              <Box variant="h3">{t(i18nKeys.newEvent.headers.backupChallenges)}</Box>
              {!!backupChallengeListItems.length
                ? backupChallengeListItems.map((c, idx) => getChallengeListItem(c, idx, true))
                : t(i18nKeys.general.none)}
            </SpaceBetween>

            {/* Right column - Excess challenges */}
            {!!excessChallengeListItems.length && (
              <SpaceBetween direction="vertical" size="l">
                <Box variant="h3">{t(i18nKeys.aiEventCreator.additionalSuggestions)}</Box>
                {excessChallengeListItems.map((c, idx) => getChallengeListItem(c, idx, false, true))}
              </SpaceBetween>
            )}
          </Grid>
        )}
      </Container>
    );
  }
};

const Wrapper: React.FC = ({ children }) => {
  const { t } = useTranslation();
  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerVariant="high-contrast"
      header={
        <Box padding={'l'}>
          <Header variant="h1" description={t(i18nKeys.aiEventCreator.longDescription)}>
            {t(i18nKeys.aiEventCreator.title)}
          </Header>
        </Box>
      }
    >
      <Box padding={'m'}>{children}</Box>
    </ContentLayout>
  );
};

const Total: React.FC<{ onChangeUpdateDelayMS?: number }> = ({ onChangeUpdateDelayMS = 150 }) => (
  <Wrapper>
    <NewAiEvent onChangeUpdateDelayMS={onChangeUpdateDelayMS} />
  </Wrapper>
);

export default Total;
